import { DEFAULT_CONFIG } from "./defaults";
import type { RoomLink } from "./link";
import type { Config, ConfigLayer } from "./types";
import { validateLayer } from "./validate";

/** localStorage key for user overrides (settings UI). */
export const CONFIG_STORAGE_KEY = "codeshare:config";
/** Runtime config file, so self-hosters can change settings without rebuilding. */
export const CONFIG_URL = "/config.json";

export interface LoadConfigOptions {
  /**
   * Build-time env (import.meta.env): VITE_ICE_SERVERS, VITE_SIGNALING, VITE_ICE_TRANSPORT_POLICY,
   * VITE_IROH_RELAYS.
   */
  env?: Record<string, unknown>;
  fetch?: (
    url: string,
    init?: { signal?: AbortSignal },
  ) => Promise<{
    ok: boolean;
    headers?: { get(name: string): string | null };
    json(): Promise<unknown>;
  }>;
  configUrl?: string;
  storage?: { getItem(key: string): string | null } | null;
  /** The room link being joined: its signaling (only) overrides everything else. */
  link?: RoomLink;
  /** How long to wait for /config.json (default 5 s: it's fetched while the app is starting). */
  timeoutMs?: number;
}

export interface LoadedConfig {
  config: Config;
  warnings: string[];
}

/** Later layers replace earlier ones field by field. */
export function mergeLayers(...layers: ConfigLayer[]): Config {
  return Object.assign({}, DEFAULT_CONFIG, ...layers);
}

function envLayer(env: Record<string, unknown>, warn: (m: string) => void): ConfigLayer {
  const raw: Record<string, unknown> = {};
  const json = (name: string, key: string) => {
    const value = env[name];
    if (typeof value !== "string" || value === "") return;
    try {
      raw[key] = JSON.parse(value);
    } catch {
      warn(`ignoring ${name}: not valid JSON`);
    }
  };
  json("VITE_ICE_SERVERS", "iceServers");
  json("VITE_SIGNALING", "signaling");
  json("VITE_IROH_RELAYS", "irohRelays");
  if (typeof env.VITE_ICE_TRANSPORT_POLICY === "string" && env.VITE_ICE_TRANSPORT_POLICY) {
    raw.iceTransportPolicy = env.VITE_ICE_TRANSPORT_POLICY;
  }
  // Build-time env is trusted (test builds may use in-process signaling).
  return validateLayer(raw, (m) => warn(`env: ${m}`), { allowMemory: true });
}

async function fileLayer(
  options: LoadConfigOptions,
  warn: (m: string) => void,
): Promise<ConfigLayer> {
  if (!options.fetch) return {};
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? 5000);
  try {
    const response = await options.fetch(options.configUrl ?? CONFIG_URL, {
      signal: controller.signal,
    });
    if (!response.ok) return {}; // no config.json deployed: normal
    // Single-page hosting answers unknown paths with index.html: that's "no config.json" too.
    if (response.headers?.get("content-type")?.includes("text/html")) return {};
    return validateLayer(await response.json(), (m) => warn(`config.json: ${m}`));
  } catch {
    warn("config.json: unavailable or not valid JSON; using defaults");
    return {};
  } finally {
    clearTimeout(timer);
  }
}

function storageLayer(options: LoadConfigOptions, warn: (m: string) => void): ConfigLayer {
  let raw: string | null | undefined;
  try {
    raw = options.storage?.getItem(CONFIG_STORAGE_KEY);
  } catch {
    return {};
  }
  if (!raw) return {};
  try {
    return validateLayer(JSON.parse(raw), (m) => warn(`settings: ${m}`));
  } catch {
    warn("settings: not valid JSON");
    return {};
  }
}

/** Resolves the effective config: defaults ← env ← /config.json ← settings ← room link. */
export async function loadConfig(options: LoadConfigOptions = {}): Promise<LoadedConfig> {
  const warnings: string[] = [];
  const warn = (message: string) => warnings.push(message);
  const config = mergeLayers(
    envLayer(options.env ?? {}, warn),
    await fileLayer(options, warn),
    storageLayer(options, warn),
    options.link?.signaling ? { signaling: options.link.signaling } : {},
  );
  return { config, warnings };
}
